// Generated by TaroBase SDK Generator: 2026-05-10T07:46:35.104Z
// Collection: rounds/$roundId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * A race round with a 2-min lottery phase and 3-min racing phase. Managed by backend.
 */

export interface RoundsRequest {
  roundId: string;
  status: string;
  lotteryStartTime: number | TimeOperation | IncrementOperation | TokenAmount;
  raceStartTime: number | TimeOperation | IncrementOperation | TokenAmount;
  raceEndTime: number | TimeOperation | IncrementOperation | TokenAmount;
  winnerCoinId: string;
  totalPicks: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface RoundsResponse {
  roundId: string;
  status: string;
  lotteryStartTime: number;
  raceStartTime: number;
  raceEndTime: number;
  winnerCoinId: string;
  totalPicks: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Rounds operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildRounds(roundId: string, data: RoundsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rounds/${roundId}`, document: doc };
}

/**
 * Only the backend (PROJECT_VAULT_ADDRESS) creates new rounds via the heartbeat orchestrator. Frontend should pass winnerCoinId='' and totalPicks=0 on create. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setRounds(roundId: string, data: RoundsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rounds/${roundId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Rounds: ${formatError(error)}`);
    return false;
  }
}

export type RoundsRequestUpdate = Partial<RoundsRequest>;

/**
 * Build a Rounds update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateRounds(roundId: string, data: RoundsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rounds/${roundId}`, document: doc };
}

/**
 * Only the backend (PROJECT_VAULT_ADDRESS) advances status (lottery -> racing -> finished), sets winnerCoinId, and increments totalPicks. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateRounds(roundId: string, data: RoundsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rounds/${roundId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Rounds: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read round state to display the current race.
   (Get Single Item)
 */
export async function getRounds(roundId: string): Promise<RoundsResponse | null> {
  try {
    const result = await get(`rounds/${roundId}`);
    return result as RoundsResponse | null;
  } catch (error) {
    console.error(`Error getting Rounds: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Rounds document. (
  Read Operation Details: Anyone can read round state to display the current race.
  )
 */
export function subscribeRounds(callback: (data: RoundsResponse | null) => void, roundId: string): Promise<() => Promise<void>> {
  return subscribe(`rounds/${roundId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as RoundsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Rounds at path '${`rounds/${roundId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Rounds items from collection rounds
 
  Read Operation Details: Anyone can read round state to display the current race.
  
 */
export async function getManyRounds(filter: string = ""): Promise<RoundsResponse[]> {
  try {
    const results = await get("rounds", { prompt: filter });
    return Array.isArray(results) ? results as RoundsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Rounds collection at path 'rounds': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Rounds collection at rounds
 
  Read Operation Details: Anyone can read round state to display the current race.
  
 */
export function subscribeManyRounds(callback: (data: RoundsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("rounds", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RoundsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Rounds collection at path '${"rounds"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Rounds items from collection rounds
 
  Read Operation Details: Anyone can read round state to display the current race.
  
 */
export async function getAllRounds(filter: string = ""): Promise<RoundsResponse[]> {
  try {
    const results = await get("rounds", { prompt: filter });
    return Array.isArray(results) ? results as RoundsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Rounds collection at path 'rounds': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Rounds collection at rounds
 
  Read Operation Details: Anyone can read round state to display the current race.
  
 */
export function subscribeAllRounds(callback: (data: RoundsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("rounds", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RoundsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Rounds collection at path '${"rounds"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Rounds items in collection rounds.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countRounds(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("rounds", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Rounds at path 'rounds': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Rounds items in collection rounds.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateRounds(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("rounds", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Rounds at path 'rounds': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the backend (PROJECT_VAULT_ADDRESS) can delete rounds (e.g., for cleanup).
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteRounds(roundId: string): Promise<boolean> {
  try {
    const succeeded = await set(`rounds/${roundId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Rounds: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Rounds for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteRounds(roundId: string): DocumentOperation {
  return { path: `rounds/${roundId}`, document: null };
}


/**
 * Coins selected to race in a given round. Populated by backend when lottery ends; price/performance updated during racing phase.
 */

export interface RoundsRacersRequest {
  coinId: string;
  symbol: string;
  name: string;
  imageUrl: string;
  startPrice: string;
  currentPrice: string;
  performancePct: string;
  priceHistoryJson: string;
}

export interface RoundsRacersResponse {
  coinId: string;
  symbol: string;
  name: string;
  imageUrl: string;
  startPrice: string;
  currentPrice: string;
  performancePct: string;
  priceHistoryJson: string;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a RoundsRacers operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildRoundsRacers(roundId: string, coinId: string, data: RoundsRacersRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rounds/${roundId}/racers/${coinId}`, document: doc };
}

/**
 * Only the backend (PROJECT_VAULT_ADDRESS) populates the 6 racers per round when the lottery phase ends. Frontend should default priceHistoryJson to '[]'. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setRoundsRacers(roundId: string, coinId: string, data: RoundsRacersRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rounds/${roundId}/racers/${coinId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting RoundsRacers: ${formatError(error)}`);
    return false;
  }
}

export type RoundsRacersRequestUpdate = Partial<RoundsRacersRequest>;

/**
 * Build a RoundsRacers update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateRoundsRacers(roundId: string, coinId: string, data: RoundsRacersRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rounds/${roundId}/racers/${coinId}`, document: doc };
}

/**
 * Only the backend (PROJECT_VAULT_ADDRESS) updates currentPrice, performancePct, and appends to priceHistoryJson during the racing phase. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateRoundsRacers(roundId: string, coinId: string, data: RoundsRacersRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rounds/${roundId}/racers/${coinId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating RoundsRacers: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read racer data to render the live race UI.
   (Get Single Item)
 */
export async function getRoundsRacers(roundId: string, coinId: string): Promise<RoundsRacersResponse | null> {
  try {
    const result = await get(`rounds/${roundId}/racers/${coinId}`);
    return result as RoundsRacersResponse | null;
  } catch (error) {
    console.error(`Error getting RoundsRacers: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single RoundsRacers document. (
  Read Operation Details: Anyone can read racer data to render the live race UI.
  )
 */
export function subscribeRoundsRacers(callback: (data: RoundsRacersResponse | null) => void, roundId: string, coinId: string): Promise<() => Promise<void>> {
  return subscribe(`rounds/${roundId}/racers/${coinId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as RoundsRacersResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to RoundsRacers at path '${`rounds/${roundId}/racers/${coinId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many RoundsRacers items from collection rounds/${roundId}/racers
 
  Read Operation Details: Anyone can read racer data to render the live race UI.
  
 */
export async function getManyRoundsRacers(roundId: string, filter: string = ""): Promise<RoundsRacersResponse[]> {
  try {
    const results = await get(`rounds/${roundId}/racers`, { prompt: filter });
    return Array.isArray(results) ? results as RoundsRacersResponse[] : [];
  } catch (error) {
    console.error(`Error getting RoundsRacers collection at path 'rounds/${roundId}/racers': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in RoundsRacers collection at rounds/${roundId}/racers
 
  Read Operation Details: Anyone can read racer data to render the live race UI.
  
 */
export function subscribeManyRoundsRacers(callback: (data: RoundsRacersResponse[]) => void, roundId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`rounds/${roundId}/racers`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RoundsRacersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to RoundsRacers collection at path '${`rounds/${roundId}/racers`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all RoundsRacers items from collection rounds/${roundId}/racers
 
  Read Operation Details: Anyone can read racer data to render the live race UI.
  
 */
export async function getAllRoundsRacers(roundId: string, filter: string = ""): Promise<RoundsRacersResponse[]> {
  try {
    const results = await get(`rounds/*/racers`, { prompt: filter });
    return Array.isArray(results) ? results as RoundsRacersResponse[] : [];
  } catch (error) {
    console.error(`Error getting RoundsRacers collection at path 'rounds/${roundId}/racers': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in RoundsRacers collection at rounds/${roundId}/racers
 
  Read Operation Details: Anyone can read racer data to render the live race UI.
  
 */
export function subscribeAllRoundsRacers(callback: (data: RoundsRacersResponse[]) => void, roundId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`rounds/*/racers`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RoundsRacersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to RoundsRacers collection at path '${`rounds/*/racers`}': ${formatError(error)}`);
    }
  });
}

/**
 * Count RoundsRacers items in collection rounds/${roundId}/racers.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countRoundsRacers(roundId: string, filter: string = ""): Promise<AggregateResult> {
  try {
    return await count(`rounds/${roundId}/racers`, { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting RoundsRacers at path 'rounds/${roundId}/racers': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on RoundsRacers items in collection rounds/${roundId}/racers.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateRoundsRacers(roundId: string, operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate(`rounds/${roundId}/racers`, operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating RoundsRacers at path 'rounds/${roundId}/racers': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the backend (PROJECT_VAULT_ADDRESS) can remove racers.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteRoundsRacers(roundId: string, coinId: string): Promise<boolean> {
  try {
    const succeeded = await set(`rounds/${roundId}/racers/${coinId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting RoundsRacers: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for RoundsRacers for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteRoundsRacers(roundId: string, coinId: string): DocumentOperation {
  return { path: `rounds/${roundId}/racers/${coinId}`, document: null };
}


/**
 * A user's single pick (coin) for a given round. Created during the lottery phase. Immutable once placed.
 */

export interface RoundsPicksRequest {
  userAddress: AddressType;
  coinId: string;
  pickedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface RoundsPicksResponse {
  userAddress: string;
  coinId: string;
  pickedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a RoundsPicks operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildRoundsPicks(roundId: string, userAddress: string, data: RoundsPicksRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rounds/${roundId}/picks/${userAddress}`, document: doc };
}

/**
 * Authenticated user only - @user.address must match the $userAddress path segment. The round must exist and be in either 'lottery' or 'racing' status. The chosen coinId must be one of the 6 racers in this round (verified via get(/rounds/$roundId/racers/@newData.coinId) != null). The UI enforces the 5-minute pre-race-end cutoff client-side. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setRoundsPicks(roundId: string, userAddress: string, data: RoundsPicksRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rounds/${roundId}/picks/${userAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting RoundsPicks: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read picks to display leaderboards and pick distribution.
   (Get Single Item)
 */
export async function getRoundsPicks(roundId: string, userAddress: string): Promise<RoundsPicksResponse | null> {
  try {
    const result = await get(`rounds/${roundId}/picks/${userAddress}`);
    return result as RoundsPicksResponse | null;
  } catch (error) {
    console.error(`Error getting RoundsPicks: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single RoundsPicks document. (
  Read Operation Details: Anyone can read picks to display leaderboards and pick distribution.
  )
 */
export function subscribeRoundsPicks(callback: (data: RoundsPicksResponse | null) => void, roundId: string, userAddress: string): Promise<() => Promise<void>> {
  return subscribe(`rounds/${roundId}/picks/${userAddress}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as RoundsPicksResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to RoundsPicks at path '${`rounds/${roundId}/picks/${userAddress}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many RoundsPicks items from collection rounds/${roundId}/picks
 
  Read Operation Details: Anyone can read picks to display leaderboards and pick distribution.
  
 */
export async function getManyRoundsPicks(roundId: string, filter: string = ""): Promise<RoundsPicksResponse[]> {
  try {
    const results = await get(`rounds/${roundId}/picks`, { prompt: filter });
    return Array.isArray(results) ? results as RoundsPicksResponse[] : [];
  } catch (error) {
    console.error(`Error getting RoundsPicks collection at path 'rounds/${roundId}/picks': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in RoundsPicks collection at rounds/${roundId}/picks
 
  Read Operation Details: Anyone can read picks to display leaderboards and pick distribution.
  
 */
export function subscribeManyRoundsPicks(callback: (data: RoundsPicksResponse[]) => void, roundId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`rounds/${roundId}/picks`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RoundsPicksResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to RoundsPicks collection at path '${`rounds/${roundId}/picks`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all RoundsPicks items from collection rounds/${roundId}/picks
 
  Read Operation Details: Anyone can read picks to display leaderboards and pick distribution.
  
 */
export async function getAllRoundsPicks(roundId: string, filter: string = ""): Promise<RoundsPicksResponse[]> {
  try {
    const results = await get(`rounds/*/picks`, { prompt: filter });
    return Array.isArray(results) ? results as RoundsPicksResponse[] : [];
  } catch (error) {
    console.error(`Error getting RoundsPicks collection at path 'rounds/${roundId}/picks': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in RoundsPicks collection at rounds/${roundId}/picks
 
  Read Operation Details: Anyone can read picks to display leaderboards and pick distribution.
  
 */
export function subscribeAllRoundsPicks(callback: (data: RoundsPicksResponse[]) => void, roundId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`rounds/*/picks`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RoundsPicksResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to RoundsPicks collection at path '${`rounds/*/picks`}': ${formatError(error)}`);
    }
  });
}

/**
 * Count RoundsPicks items in collection rounds/${roundId}/picks.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countRoundsPicks(roundId: string, filter: string = ""): Promise<AggregateResult> {
  try {
    return await count(`rounds/${roundId}/picks`, { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting RoundsPicks at path 'rounds/${roundId}/picks': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on RoundsPicks items in collection rounds/${roundId}/picks.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateRoundsPicks(roundId: string, operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate(`rounds/${roundId}/picks`, operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating RoundsPicks at path 'rounds/${roundId}/picks': ${formatError(error)}`);
    return { value: 0 };
  }
}

